import { useEffect, useRef, useState } from 'react';

import { type THUMBNAIL_SIZE } from '~/domain/shared/models/thumbnail-size';
import { cn } from '~/styles/extended-theme';

import FallbackThumbnail, { type Variant } from './fallback-thumbnail';

export default function ThumbnailWithFallback({
  size,
  url,
  variant,
  alt,
  rounded,
  showSpinnerWhileLoading = false,
  spinnerColor = 'white',
  imageClassName,
}: {
  size: THUMBNAIL_SIZE;
  url: string | null | undefined;
  variant?: Variant;
  alt?: string;
  rounded?: boolean;
  showSpinnerWhileLoading?: boolean;
  spinnerColor?: 'white' | 'primary';
  imageClassName?: string;
}) {
  const MAX_RETRIES = 5;
  const [, setHasError] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const numRetries = useRef(0);
  const [imgElRetries, setImgElRetries] = useState(0);

  useEffect(() => {
    if (!url) return;

    const handleImageLoad = () => {
      setImageLoaded(true);
    };

    const handleImageError = () => {
      if (numRetries.current <= MAX_RETRIES) {
        numRetries.current = numRetries.current + 1;
        setTimeout(() => loadImage(url), 1500 + 500 * numRetries.current);
      } else {
        setHasError(true);
      }
    };

    const loadImage = (url: string) => {
      const img = new Image();
      img.onload = handleImageLoad;
      img.onerror = handleImageError;
      img.src = url;
    };

    loadImage(url);
  }, [url]);

  return (
    <>
      {imageLoaded && url ? (
        <img
          src={url}
          alt={alt}
          className={cn(
            'max-h-full max-w-full object-contain',
            rounded ? 'rounded-2xl' : null,
            imageClassName,
          )}
          key={`${url}-${imgElRetries}`}
          onError={() => {
            if (imgElRetries <= 3) {
              setTimeout(() => setImgElRetries(imgElRetries + 1), 1500);
            }
          }}
        />
      ) : (
        <FallbackThumbnail
          size={size}
          alt={alt}
          rounded={rounded}
          variant={variant}
          showSpinner={showSpinnerWhileLoading}
          spinnerColor={spinnerColor}
          imageClassName={imageClassName}
        />
      )}
    </>
  );
}
